import { designDeployPatch, designDestroyPatch } from '../../../config/designFixtures';
import {
  deleteCascadePhases,
  deployAllLayers,
  deployUntilLayers,
  destroyAllLayers,
  destroyFromLayers,
  isProvisioned,
  nodeDeployStatus,
} from './cascadeGraph';

export const DESIGN_HARNESS_STEP_MS = 280;

export function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    if (!signal) return;
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

export function upstreamOutputsFor(nodeId, nodes, edges) {
  const out = {};
  for (const edge of edges || []) {
    if (edge.target !== nodeId) continue;
    const src = (nodes || []).find((n) => n.id === edge.source);
    const data = src?.data?.output_data || {};
    const handle = edge.sourceHandle;
    out[edge.targetHandle] =
      data[handle] ||
      data.thought ||
      data.result ||
      data.tick ||
      data.handle ||
      data.combined ||
      data.resource ||
      null;
  }
  return out;
}

function replaceNodes(list, id, patch) {
  return list.map((node) =>
    node.id === id ? { ...node, data: { ...node.data, ...patch } } : node
  );
}

/**
 * Design-mode bash harness: walk deploy-until order and fabricate apply output.
 */
async function walkDeploy({
  layers,
  nodes,
  edges,
  setNodes,
  stepMs = DESIGN_HARNESS_STEP_MS,
  wait = sleep,
  onStep,
  signal,
}) {
  let current = nodes;
  for (const layer of layers || []) {
    if (signal?.aborted) return current;
    const pending = layer.filter((id) => {
      const node = current.find((n) => n.id === id);
      return node && nodeDeployStatus(node) !== 'DEPLOYED';
    });
    if (!pending.length) continue;
    for (const id of pending) {
      const node = current.find((n) => n.id === id);
      const label = node?.data?.label || id;
      current = replaceNodes(current, id, { deploy_status: 'DEPLOYING' });
      onStep?.({ action: 'provision', id, label, status: 'DEPLOYING' });
    }
    setNodes(current);
    if (stepMs > 0) await wait(stepMs, signal);
    if (signal?.aborted) return current;
    for (const id of pending) {
      const latest = current.find((n) => n.id === id);
      const label = latest?.data?.label || id;
      const patch = designDeployPatch(latest?.data || latest, {
        upstreamOutputs: upstreamOutputsFor(id, current, edges),
      });
      current = replaceNodes(current, id, patch);
      onStep?.({ action: 'provision', id, label, status: 'DEPLOYED' });
    }
    setNodes(current);
  }
  return current;
}

async function walkDestroy({
  layers,
  nodes,
  setNodes,
  stepMs = DESIGN_HARNESS_STEP_MS,
  wait = sleep,
  onStep,
  signal,
}) {
  let current = nodes;
  for (const layer of layers || []) {
    if (signal?.aborted) return current;
    const pending = layer.filter((id) => {
      const node = current.find((n) => n.id === id);
      return node && isProvisioned(nodeDeployStatus(node));
    });
    if (!pending.length) continue;
    for (const id of pending) {
      const node = current.find((n) => n.id === id);
      const label = node?.data?.label || id;
      current = replaceNodes(current, id, { deploy_status: 'DESTROYING' });
      onStep?.({ action: 'deprovision', id, label, status: 'DESTROYING' });
    }
    setNodes(current);
    if (stepMs > 0) await wait(stepMs, signal);
    if (signal?.aborted) return current;
    for (const id of pending) {
      const node = current.find((n) => n.id === id);
      const label = node?.data?.label || id;
      current = replaceNodes(current, id, designDestroyPatch());
      onStep?.({ action: 'deprovision', id, label, status: 'NOT_DEPLOYED' });
    }
    setNodes(current);
  }
  return current;
}

export async function runDesignDeployUntil({
  leafId,
  nodes,
  edges,
  setNodes,
  stepMs = DESIGN_HARNESS_STEP_MS,
  wait = sleep,
  onStep,
  signal,
}) {
  return walkDeploy({
    layers: deployUntilLayers(leafId, edges),
    nodes,
    edges,
    setNodes,
    stepMs,
    wait,
    onStep,
    signal,
  });
}

export async function runDesignDeployAll({
  nodes,
  edges,
  setNodes,
  stepMs = DESIGN_HARNESS_STEP_MS,
  wait = sleep,
  onStep,
  signal,
}) {
  return walkDeploy({
    layers: deployAllLayers(
      (nodes || []).map((n) => n.id),
      edges
    ),
    nodes,
    edges,
    setNodes,
    stepMs,
    wait,
    onStep,
    signal,
  });
}

/**
 * Design-mode bash harness: walk destroy-from order and clear output.
 */
export async function runDesignDestroyFrom({
  rootId,
  nodes,
  edges,
  setNodes,
  stepMs = DESIGN_HARNESS_STEP_MS,
  wait = sleep,
  onStep,
  signal,
}) {
  return walkDestroy({
    layers: destroyFromLayers(rootId, edges),
    nodes,
    setNodes,
    stepMs,
    wait,
    onStep,
    signal,
  });
}

export async function runDesignDestroyAll({
  nodes,
  edges,
  setNodes,
  stepMs = DESIGN_HARNESS_STEP_MS,
  wait = sleep,
  onStep,
  signal,
}) {
  return walkDestroy({
    layers: destroyAllLayers(
      (nodes || []).map((n) => n.id),
      edges
    ),
    nodes,
    setNodes,
    stepMs,
    wait,
    onStep,
    signal,
  });
}

/**
 * Downstream nodes out, then edges, then the upstream node.
 */
export async function runDesignDeleteCascade({
  rootId,
  nodes,
  edges,
  setNodes,
  setEdges,
  stepMs = DESIGN_HARNESS_STEP_MS,
  wait = sleep,
}) {
  const current = await runDesignDestroyFrom({
    rootId,
    nodes,
    edges,
    setNodes,
    stepMs,
    wait,
  });
  const { downstream, edgeIds, root } = deleteCascadePhases(rootId, edges);
  const downSet = new Set(downstream);
  const remainingNodes = current.filter((n) => !downSet.has(n.id));
  setNodes(remainingNodes);
  if (stepMs > 0) await wait(stepMs);
  const edgeSet = new Set(edgeIds);
  setEdges((eds) => eds.filter((e) => !edgeSet.has(e.id)));
  if (stepMs > 0) await wait(stepMs);
  setNodes((nds) => nds.filter((n) => n.id !== root));
  return { downstream, edgeIds, root };
}
